import { logError } from "../logging";

export const NOT_ALLOWED_ERROR_NAME = "NotAllowedError";
export const NOT_FOUND_ERROR_NAME = "NotFoundError";
export const NOT_READABLE_ERROR_NAME = "NotReadableError";
export const OVERCONSTRAINED_ERROR_NAME = "OverconstrainedError";

// Older browsers might not implement mediaDevices at all, so we set an empty object first
if (navigator.mediaDevices === undefined) {
  (navigator as any).mediaDevices = {};
}

// Some browsers partially implement mediaDevices. We can't just assign an object
// with getUserMedia as it would overwrite existing properties.
// Here, we will just add the getUserMedia property if it's missing.
if (navigator.mediaDevices.getUserMedia === undefined) {
  navigator.mediaDevices.getUserMedia = function (
    constraints: MediaStreamConstraints
  ): Promise<MediaStream> {
    // First get ahold of the legacy getUserMedia, if present
    const getUserMedia =
      (navigator as any).webkitGetUserMedia || (navigator as any).mozGetUserMedia;

    // Some browsers just don't implement it - return a rejected promise with an error
    // to keep a consistent interface
    if (!getUserMedia) {
      return Promise.reject(new Error("getUserMedia is not implemented in this browser"));
    }

    // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
    return new Promise<MediaStream>((resolve, reject) => {
      getUserMedia.call(navigator, constraints, resolve, reject);
    });
  };
}

export interface MediaDeviceErrorResult {
  audioError?: Error;
  videoError?: Error;
}

function checkMediaPermission(mediaType: "audio" | "video"): Promise<void> {
  return new Promise((resolve, reject) => {
    const constraints: MediaStreamConstraints = { [mediaType]: true };
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(stream => {
        stopMediaStream(stream);
        resolve();
      })
      .catch((err: Error) => {
        reject(err);
      });
  });
}

export async function checkPermissions(): Promise<MediaDeviceErrorResult> {
  const result: MediaDeviceErrorResult = {};

  try {
    await checkMediaPermission("audio");
  } catch (error) {
    result.audioError = error as Error;
    logError("During audio permission check received error", {
      err: result.audioError,
      details: getMediaErrorDetails(result.audioError),
      device: "audio",
    });
  }

  try {
    await checkMediaPermission("video");
  } catch (error) {
    result.videoError = error as Error;
    logError("During video permission check received error", {
      err: result.videoError,
      details: getMediaErrorDetails(result.videoError),
      device: "video",
    });
  }

  return result;
}

export function addOnDeviceChangeListener(listener: EventListener): void {
  navigator.mediaDevices.addEventListener("devicechange", listener);
}

export function removeOnDeviceChangeListener(listener: EventListener): void {
  navigator.mediaDevices.removeEventListener("devicechange", listener);
}

export function stopMediaStream(stream: MediaStream | null): void {
  if (!stream) {
    return;
  }
  stream.getAudioTracks &&
    stream.getAudioTracks().forEach(track => {
      track && track.stop && typeof track.stop === "function" && track.stop();
    });
  stream.getVideoTracks &&
    stream.getVideoTracks().forEach(track => {
      track && track.stop && typeof track.stop === "function" && track.stop();
    });
}

interface MediaErrorDetails {
  description?: string;
  result?: string;
}

export function getMediaErrorDetails(error: Error): MediaErrorDetails {
  let details: MediaErrorDetails = {};
  switch (error.name) {
    case NOT_ALLOWED_ERROR_NAME:
      details.description =
        "The user has specified that the current browsing instance is not permitted access " +
        "to the device; or the user has denied access for the current session; or the user has denied " +
        "all access to user media devices globally.";
      details.result = "Talaera doesn't have permission to access the camera/mic";
      break;
    case NOT_FOUND_ERROR_NAME:
      details.description = "No web cam and/or microphone.";
      details.result = "Camera/mic not detected";
      break;
    case NOT_READABLE_ERROR_NAME:
      details.description =
        "Although the user granted permission to use the matching devices, a hardware error " +
        "occurred at the operating system, browser, or Web page level which prevented access to the device.";
      details.result = "Camera/mic is possibly in use by some other app";
      break;
    case OVERCONSTRAINED_ERROR_NAME:
      details.description =
        "The specified constraints resulted in no candidate devices which met the criteria requested. " +
        `(${(error as any).constraint}).`;
      break;
    default:
  }
  return details;
}

export function askedPermissionsBefore(
  callback: (result: boolean | undefined) => void
): void {
  // This navigator.permissions is experimental API, we can check permissions for camera and mic only in Chrome.
  if (
    !navigator.permissions ||
    !navigator.permissions.query ||
    typeof navigator.permissions.query !== "function"
  ) {
    return callback(undefined);
  }
  const queryCamera = navigator.permissions.query({ name: "camera" as PermissionName });
  const queryMic = navigator.permissions.query({ name: "microphone" as PermissionName });
  Promise.all([queryCamera, queryMic]).then(
    function (results) {
      callback(results[0].state !== "prompt" && results[1].state !== "prompt");
    },
    function (errors) {
      // eslint-disable-next-line no-console
      console.log(errors);
      callback(undefined);
    }
  );
}

export function isNotAllowedError(error: Error): boolean {
  return error && error.name === NOT_ALLOWED_ERROR_NAME;
}

export function isNotFoundError(error: Error): boolean {
  return error && error.name === NOT_FOUND_ERROR_NAME;
}

export function isNotReadableError(error: Error): boolean {
  return error && error.name === NOT_READABLE_ERROR_NAME;
}

export function isOverconstrainedError(error: Error): boolean {
  return error && error.name === OVERCONSTRAINED_ERROR_NAME;
}
